import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    Button,
    Flex,
    Stack,
} from "@chakra-ui/react";
import { AddIcon } from "@chakra-ui/icons";
import { FaEdit, FaTrashAlt } from "react-icons/fa";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import ConfirmDialog from "components/Dialog/ConfirmDialog";
import ListPageCardHeader from "components/Card/ListPageCardHeader";
import SearchTable2 from "components/Tables/SearchTable2";
import Loading from "components/Loading/Loading";
import { useCustomToast } from "components/CustomToast/CustomToast";
import { ROUTES } from "constants/routes-path";
import { documentsService } from "services/documentsService";
import { DocumentsFilter } from "./DocumentsFilter";
import { getEmptyPageResponse, toTablePageableData } from "utils/utils";
import ListPageCardUserHeader from "../Users/ListPageCardUserHeader";
import { userService } from "../../../services/userService";

const DocumentsList = () => {
    const { t } = useTranslation();
    const [urlSearchParams] = useSearchParams();
    const [response, setResponse] = useState(undefined);
    const [userData, setUserData] = useState(undefined);
    const [searchParams, setSearchParams] = useState({});
    const navigate = useNavigate();
    const showToast = useCustomToast();
    const [selectedItem, setSelectedItem] = useState(null);
    const dialogRef = useRef(undefined);

    const parentId = urlSearchParams.get("parent_id");
    const parentType = urlSearchParams.get("parent_type");

    const handleDeleteClick = (itemId) => {
        setSelectedItem(itemId);
        dialogRef.current?.displayDialog({
            title: t("documents.list.actions.remove"),
            message: t("system.actions.remove-confirm"),
        });
    };

    const handleConfirmDelete = async () => {
        try {
            await documentsService.delete(selectedItem);
            showToast({ title: t('documents.messages.success.delete'), status: "success" });
            fetchData();
        } catch (error) {
            showToast({ title: t(error), status: "error" });
        }
    };

    const columns = useMemo(() => ([
        { Header: t("documents.list.table.header.type"), accessor: "tipo_documento" },
        { Header: t("documents.list.table.header.document-date"), accessor: "data_documento" },
        { Header: t("documents.list.table.header.expiration-date"), accessor: "data_validade" },
        { Header: t("documents.list.table.header.creation-date"), accessor: "data_criacao" },
        { Header: t("documents.list.table.header.created-by"), accessor: "nome_usuario_criacao", id: "id_usuario_criacao" },
        {
            Header: t("documents.list.table.header.operations"),
            disableSortBy: true,
            actions: [
                {
                    label: t('system.actions.edit'),
                    onClick: (row) => {
                        const queryParams = new URLSearchParams({
                            parent_id: parentId,
                            parent_type: parentType
                        }).toString();

                        navigate(`${ROUTES.documentsEditPath}/${row.original.id}?${queryParams}`, {
                            state: { parentId, parentType }
                        });
                    },
                    icon: <FaEdit />
                },
                {
                    label: t('system.actions.remove'),
                    onClick: (row) => handleDeleteClick(row.original.id),
                    icon: <FaTrashAlt color="#E53E3E" />,
                }
            ]
        },
    ]), []);

    const fetchData = async (params = {}) => {
        params.orderBy = params.orderBy ?? "data_criacao";
        params.orderDirection = params.orderDirection ?? "desc";
        params.page = Math.min(params?.page ?? 1, response?.total_pages ?? Infinity) || 1;
        if (response) setResponse(undefined);

        try {
            params = { ...searchParams, ...params };
            setSearchParams(params);
            const data = await documentsService.list(params, parentType, parentId);
            setResponse(data);
            if (parentType==="usuarios"){
                const user = await userService.find(parentId);
                setUserData(user);
            }

        } catch (err) {
            setResponse(getEmptyPageResponse(searchParams));
            showToast({ title: t(err || "documents.messages.errors.list"), status: "error" });
        }
    };

    useEffect(() => {
        fetchData();
    }, [parentId, parentType]);

    return (
        <Flex direction="column" gap="5" pt={{ base: "150px", lg: "90px" }}>
            <Flex w="100%" px="30px" justify="space-between" align="center" gap="4" direction={{ sm: "column", md: "row" }}>
                <Button
                    w="160px"
                    variant="list-primary"
                    leftIcon={<AddIcon fontSize="sm" />}
                    alignSelf={{ sm: "flex-start", lg: null }}
                    onClick={() => {
                        navigate(`${ROUTES.documentsAddPath}?parent_id=${parentId}&parent_type=${parentType}`, {
                            state: { parentId, parentType }
                        });
                    }}
                >
                    {t("documents.list.actions.new")}
                </Button>
                <Stack direction="row" spacing="10px" alignSelf={{ sm: "flex-start", lg: "auto" }}>
                    <DocumentsFilter onFilter={(filters) => fetchData({ filters })} parentType={parentType} />
                </Stack>
            </Flex>

            <Card overflowX={{ sm: "scroll", lg: "hidden" }}>
                {userData &&  (
                    <ListPageCardUserHeader userData={userData} />
                )}
                <ListPageCardHeader title={t("documents.title")} subtitle={t("documents.description")} />
                <CardBody>
                    {response ? (
                        <SearchTable2
                            columnsData={columns}
                            tableData={response.content}
                            pageableData={toTablePageableData(response, { defaultField: "data_criacao", ...searchParams })}
                            onChange={fetchData}
                            disableSearch={true}
                        />
                    ) : (
                        <Loading maxH={{ sm: "100px", md: "350px" }} />
                    )}
                </CardBody>
            </Card>

            <ConfirmDialog ref={dialogRef} onConfirm={handleConfirmDelete} confirmButtonProps={{ colorScheme: "red" }} />
        </Flex>
    );
}

export default DocumentsList;
